import React, { useMemo } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addMonths, subMonths } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import { Event } from '../types/supabase';
import { getOccurrenceDates } from '../lib/recurrence';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface CalendarProps {
  events: Event[];
  onEventSelect: (event: Event) => void;
}

const Calendar: React.FC<CalendarProps> = ({ events, onEventSelect }) => {
  const calendarEvents = useMemo(() => {
    const now = new Date();
    const rangeStart = subMonths(now, 6);
    const rangeEnd = addMonths(now, 18);
    return events.flatMap(event =>
      (event.is_recurring ? getOccurrenceDates(event, rangeStart, rangeEnd) : [event.date]).map(date => {
        const start = new Date(`${date}T${event.time}`);
        return {
          id: `${event.id}-${date}`,
          title: event.title,
          start,
          end: start,
          allDay: false,
          resource: event,
        };
      })
    );
  }, [events]);

  return (
    <div 
      className="h-[600px] bg-white rounded-lg shadow-lg p-4"
      role="region"
      aria-label="Event Calendar"
    >
      <BigCalendar
        localizer={localizer}
        events={calendarEvents}
        startAccessor="start"
        endAccessor="end"
        onSelectEvent={(event) => onEventSelect(event.resource)}
        views={['month', 'week', 'day']}
        defaultView="month"
        tooltipAccessor={(event) => event.title}
        aria-label="Event Calendar"
        messages={{
          next: "Next",
          previous: "Previous",
          today: "Today",
          month: "Month",
          week: "Week",
          day: "Day",
          noEventsInRange: "No events scheduled",
        }}
      />
    </div>
  );
};

export default Calendar;